<form [formGroup]="requestForm" (ngSubmit)="onSubmit()" class="flex flex-col gap-4 rounded-lg bg-white p-6 shadow-sm">
  <div class="grid grid-cols-3 gap-4">
    <div class="group relative">
      <label
        for="nickname"
        class="absolute -top-2 z-10 bg-white px-1 text-[12px] font-semibold text-gray-700 transition-colors group-focus-within:text-black group-hover:text-gray-900 ltr:left-3 rtl:right-3">
        <span i18n="@@pharmacyFormNicknameLabel">Nickname</span>
        <span class="text-danger-text">*</span>
      </label>
      <input
        id="nickname"
        type="text"
        formControlName="nickname"
        placeholder="Enter Pharmacy Nickname"
        i18n-placeholder="@@pharmacyFormNicknamePlaceholder"
        class="w-full rounded-[6px] border border-gray-400 bg-white py-2.5 text-[14px] text-gray-700 transition-colors outline-none placeholder:text-gray-300 hover:border-gray-600 focus:border-black ltr:px-4 rtl:px-4"
        [class.border-red-500]="requestForm.get('nickname')?.invalid && requestForm.get('nickname')?.touched" />
      @if (requestForm.get("nickname")?.invalid && requestForm.get("nickname")?.touched) {
        @if (requestForm.get("nickname")?.errors?.["required"]) {
          <span class="text-danger-text text-[12px]" i18n="@@pharmacyFormNicknameRequired">Nickname is required</span>
        }
        @if (requestForm.get("nickname")?.errors?.["minlength"]) {
          <span class="text-danger-text text-[12px]" i18n="@@pharmacyFormNicknameMinLength">
            Nickname must be at least 5 characters
          </span>
        }
        @if (requestForm.get("nickname")?.errors?.["maxlength"]) {
          <span class="text-danger-text text-[12px]" i18n="@@pharmacyFormNicknameMaxLength">
            Nickname must not exceed 25 characters
          </span>
        }
      }
    </div>
    <div class="group relative">
      <label
        for="taxId"
        class="absolute -top-2 z-10 bg-white px-1 text-[12px] font-semibold text-gray-700 transition-colors group-focus-within:text-black group-hover:text-gray-900 ltr:left-3 rtl:right-3">
        <span i18n="@@pharmacyFormTaxIdLabel">Tax Identification Number</span>
        <span class="text-danger-text">*</span>
      </label>
      <input
        id="taxId"
        type="text"
        formControlName="taxId"
        placeholder="Enter Tax Identification Number"
        i18n-placeholder="@@pharmacyFormTaxIdPlaceholder"
        class="w-full rounded-[6px] border border-gray-400 bg-white py-2.5 text-[14px] text-gray-700 transition-colors outline-none placeholder:text-gray-300 hover:border-gray-600 focus:border-black ltr:px-4 rtl:px-4"
        [class.border-red-500]="requestForm.get('taxId')?.invalid && requestForm.get('taxId')?.touched" />
      @if (requestForm.get("taxId")?.invalid && requestForm.get("taxId")?.touched) {
        @if (requestForm.get("taxId")?.errors?.["required"]) {
          <span class="text-danger-text text-[12px]" i18n="@@pharmacyFormTaxIdRequired">
            Tax Identification Number is required
          </span>
        }
        @if (requestForm.get("taxId")?.errors?.["pattern"]) {
          <span class="text-danger-text text-[12px]" i18n="@@pharmacyFormTaxIdPattern">
            Tax Identification Number must be exactly 9 digits
          </span>
        }
      }
    </div>
    <div class="group relative">
      <label
        for="address"
        class="absolute -top-2 z-10 bg-white px-1 text-[12px] font-semibold text-gray-700 transition-colors group-focus-within:text-black group-hover:text-gray-900 ltr:left-3 rtl:right-3">
        <span i18n="@@pharmacyFormAddressLabel">Address</span>
        <span class="text-danger-text">*</span>
      </label>
      <input
        id="address"
        type="text"
        formControlName="address"
        placeholder="Enter Pharmacy Address"
        i18n-placeholder="@@pharmacyFormAddressPlaceholder"
        class="w-full rounded-[6px] border border-gray-400 bg-white py-2.5 text-[14px] text-gray-700 transition-colors outline-none placeholder:text-gray-300 hover:border-gray-600 focus:border-black ltr:px-4 rtl:px-4"
        [class.border-red-500]="requestForm.get('address')?.invalid && requestForm.get('address')?.touched" />
      @if (requestForm.get("address")?.invalid && requestForm.get("address")?.touched) {
        @if (requestForm.get("address")?.errors?.["required"]) {
          <span class="text-danger-text text-[12px]" i18n="@@pharmacyFormAddressRequired">Address is required</span>
        }
        @if (requestForm.get("address")?.errors?.["maxlength"]) {
          <span class="text-danger-text text-[12px]" i18n="@@pharmacyFormAddressMaxLength">
            Address must not exceed 2000 characters
          </span>
        }
      }
    </div>
  </div>
  <div class="grid grid-cols-3 gap-4">
    <div class="flex flex-col gap-1">
      <label
        for="commercialInput"
        class="text-[14px] font-[600] hover:cursor-pointer"
        i18n="@@pharmacyFormCommercialLabel">
        Commercial Registration
      </label>
      @if (!commercialRegistrationFile) {
        <div
          class="border-border hover-border-gray flex h-[140px] flex-col items-center justify-center gap-2 rounded-[6px] border-2 border-dashed p-4"
          [class.border-primary]="isDraggingCommercial"
          (dragover)="onDragOver($event, 'commercial')"
          (dragleave)="onDragLeave($event, 'commercial')"
          (drop)="onDrop($event, 'commercial')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 24 24" class="text-gray">
            <path
              fill="currentColor"
              d="M11 16V7.85l-2.6 2.6L7 9l5-5l5 5l-1.4 1.45l-2.6-2.6V16zm-5 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
          </svg>
          <p class="text-neutral-text text-center text-[10px]" i18n="@@pharmacyFormUploadHint">
            Click to upload or drag and drop
          </p>
          <p class="text-gray text-[10px]" i18n="@@pharmacyFormMaxSizeHint">Max file Size: 5MB</p>
          <button
            type="button"
            (click)="commercialInput.click()"
            class="bg-primary hover-bg-primary-hover rounded-[6px] px-4 py-1.5 text-[12px] font-[500] text-white hover:cursor-pointer"
            i18n="@@pharmacyFormBrowseButton">
            Browse
          </button>
          <input
            id="commercialInput"
            #commercialInput
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            (change)="onFileSelect($event, 'commercial')"
            class="hidden" />
        </div>
      } @else {
        <div
          class="border-border relative flex h-[140px] flex-col items-center justify-center gap-2 rounded-[8px] border p-4">
          <span class="truncate text-center text-[12px] font-[500]">{{ commercialRegistrationFile.name }}</span>
          <span class="text-neutral-text text-[10px]">
            ({{ (commercialRegistrationFile.size || 0) / 1024 / 1024 | number: "1.2-2" }} MB)
          </span>
          <button
            type="button"
            (click)="removeFile('commercial')"
            class="text-primary hover-text-primary-hover hover:cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" />
            </svg>
          </button>
        </div>
      }
      @if (commercialRegistrationError) {
        <span class="text-danger-text text-[10px]">{{ commercialRegistrationError }}</span>
      }
    </div>
    <div class="flex flex-col gap-1">
      <label for="taxInput" class="text-[14px] font-[600] hover:cursor-pointer" i18n="@@pharmacyFormTaxCertLabel">
        Tax Certificate
      </label>
      @if (!taxRegistrationCertificateFile) {
        <div
          class="border-border hover-border-gray flex h-[140px] flex-col items-center justify-center gap-2 rounded-[6px] border-2 border-dashed p-4"
          [class.border-primary]="isDraggingTax"
          (dragover)="onDragOver($event, 'tax')"
          (dragleave)="onDragLeave($event, 'tax')"
          (drop)="onDrop($event, 'tax')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 24 24" class="text-gray">
            <path
              fill="currentColor"
              d="M11 16V7.85l-2.6 2.6L7 9l5-5l5 5l-1.4 1.45l-2.6-2.6V16zm-5 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
          </svg>
          <p class="text-neutral-text text-center text-[10px]" i18n="@@pharmacyFormUploadHint">
            Click to upload or drag and drop
          </p>
          <p class="text-gray text-[10px]" i18n="@@pharmacyFormMaxSizeHint">Max file Size: 5MB</p>
          <button
            type="button"
            (click)="taxInput.click()"
            class="bg-primary hover-bg-primary-hover rounded-[6px] px-4 py-1.5 text-[12px] font-[500] text-white hover:cursor-pointer"
            i18n="@@pharmacyFormBrowseButton">
            Browse
          </button>
          <input
            id="taxInput"
            #taxInput
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            (change)="onFileSelect($event, 'tax')"
            class="hidden" />
        </div>
      } @else {
        <div
          class="border-border relative flex h-[140px] flex-col items-center justify-center gap-2 rounded-[8px] border p-4">
          <span class="truncate text-center text-[12px] font-[500]">{{ taxRegistrationCertificateFile.name }}</span>
          <span class="text-neutral-text text-[10px]">
            ({{ (taxRegistrationCertificateFile.size || 0) / 1024 / 1024 | number: "1.2-2" }} MB)
          </span>
          <button
            type="button"
            (click)="removeFile('tax')"
            class="text-primary hover-text-primary-hover hover:cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" />
            </svg>
          </button>
        </div>
      }
      @if (taxRegistrationCertificateError) {
        <span class="text-danger-text text-[10px]">{{ taxRegistrationCertificateError }}</span>
      }
    </div>
    <div class="flex flex-col gap-1">
      <label for="contractInput" class="text-[14px] font-[600] hover:cursor-pointer" i18n="@@pharmacyFormContractLabel">
        Pharmacy Contract
      </label>
      @if (!pharmacyContractFile) {
        <div
          class="border-border hover-border-gray flex h-[140px] flex-col items-center justify-center gap-2 rounded-[6px] border-2 border-dashed p-4"
          [class.border-primary]="isDraggingContract"
          (dragover)="onDragOver($event, 'contract')"
          (dragleave)="onDragLeave($event, 'contract')"
          (drop)="onDrop($event, 'contract')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 24 24" class="text-gray">
            <path
              fill="currentColor"
              d="M11 16V7.85l-2.6 2.6L7 9l5-5l5 5l-1.4 1.45l-2.6-2.6V16zm-5 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
          </svg>
          <p class="text-neutral-text text-center text-[10px]" i18n="@@pharmacyFormUploadHint">
            Click to upload or drag and drop
          </p>
          <p class="text-gray text-[10px]" i18n="@@pharmacyFormMaxSizeHint">Max file Size: 5MB</p>
          <button
            type="button"
            (click)="contractInput.click()"
            class="bg-primary hover-bg-primary-hover rounded-[6px] px-4 py-1.5 text-[12px] font-[500] text-white hover:cursor-pointer"
            i18n="@@pharmacyFormBrowseButton">
            Browse
          </button>
          <input
            id="contractInput"
            #contractInput
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            (change)="onFileSelect($event, 'contract')"
            class="hidden" />
        </div>
      } @else {
        <div
          class="border-border relative flex h-[140px] flex-col items-center justify-center gap-2 rounded-[8px] border p-4">
          <span class="truncate text-center text-[12px] font-[500]">{{ pharmacyContractFile.name }}</span>
          <span class="text-neutral-text text-[10px]">
            ({{ (pharmacyContractFile.size || 0) / 1024 / 1024 | number: "1.2-2" }} MB)
          </span>
          <button
            type="button"
            (click)="removeFile('contract')"
            class="text-primary hover-text-primary-hover hover:cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" />
            </svg>
          </button>
        </div>
      }
      @if (pharmacyContractError) {
        <span class="text-danger-text text-[10px]">{{ pharmacyContractError }}</span>
      }
    </div>
    <div class="flex flex-col gap-1">
      <label for="licenseInput" class="text-[14px] font-[600] hover:cursor-pointer" i18n="@@pharmacyFormLicenseLabel">
        Pharmacy License
      </label>
      @if (!pharmacyLicenseFile) {
        <div
          class="border-border hover-border-gray flex h-[140px] flex-col items-center justify-center gap-2 rounded-[6px] border-2 border-dashed p-4"
          [class.border-primary]="isDraggingLicense"
          (dragover)="onDragOver($event, 'license')"
          (dragleave)="onDragLeave($event, 'license')"
          (drop)="onDrop($event, 'license')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 24 24" class="text-gray">
            <path
              fill="currentColor"
              d="M11 16V7.85l-2.6 2.6L7 9l5-5l5 5l-1.4 1.45l-2.6-2.6V16zm-5 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
          </svg>
          <p class="text-neutral-text text-center text-[10px]" i18n="@@pharmacyFormUploadHint">
            Click to upload or drag and drop
          </p>
          <p class="text-gray text-[10px]" i18n="@@pharmacyFormMaxSizeHint">Max file Size: 5MB</p>
          <button
            type="button"
            (click)="licenseInput.click()"
            class="bg-primary hover-bg-primary-hover rounded-[6px] px-4 py-1.5 text-[12px] font-[500] text-white hover:cursor-pointer"
            i18n="@@pharmacyFormBrowseButton">
            Browse
          </button>
          <input
            id="licenseInput"
            #licenseInput
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            (change)="onFileSelect($event, 'license')"
            class="hidden" />
        </div>
      } @else {
        <div
          class="border-border relative flex h-[140px] flex-col items-center justify-center gap-2 rounded-[8px] border p-4">
          <span class="truncate text-center text-[12px] font-[500]">{{ pharmacyLicenseFile.name }}</span>
          <span class="text-neutral-text text-[10px]">
            ({{ (pharmacyLicenseFile.size || 0) / 1024 / 1024 | number: "1.2-2" }} MB)
          </span>
          <button
            type="button"
            (click)="removeFile('license')"
            class="text-primary hover-text-primary-hover hover:cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" />
            </svg>
          </button>
        </div>
      }
      @if (pharmacyLicenseError) {
        <span class="text-danger-text text-[10px]">{{ pharmacyLicenseError }}</span>
      }
    </div>
    <div class="flex flex-col gap-1">
      <label
        for="nationalIdInput"
        class="text-[14px] font-[600] hover:cursor-pointer"
        i18n="@@pharmacyFormNationalIdLabel">
        Pharmacist National ID
      </label>
      @if (!pharmacistNationalIdFile) {
        <div
          class="border-border hover-border-gray flex h-[140px] flex-col items-center justify-center gap-2 rounded-[6px] border-2 border-dashed p-4"
          [class.border-primary]="isDraggingNationalId"
          (dragover)="onDragOver($event, 'nationalId')"
          (dragleave)="onDragLeave($event, 'nationalId')"
          (drop)="onDrop($event, 'nationalId')">
          <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 24 24" class="text-gray">
            <path
              fill="currentColor"
              d="M11 16V7.85l-2.6 2.6L7 9l5-5l5 5l-1.4 1.45l-2.6-2.6V16zm-5 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
          </svg>
          <p class="text-neutral-text text-center text-[10px]" i18n="@@pharmacyFormUploadHint">
            Click to upload or drag and drop
          </p>
          <p class="text-gray text-[10px]" i18n="@@pharmacyFormMaxSizeHint">Max file Size: 5MB</p>
          <button
            type="button"
            (click)="nationalIdInput.click()"
            class="bg-primary hover-bg-primary-hover rounded-[6px] px-4 py-1.5 text-[12px] font-[500] text-white hover:cursor-pointer"
            i18n="@@pharmacyFormBrowseButton">
            Browse
          </button>
          <input
            id="nationalIdInput"
            #nationalIdInput
            type="file"
            accept="image/png,image/jpeg,application/pdf"
            (change)="onFileSelect($event, 'nationalId')"
            class="hidden" />
        </div>
      } @else {
        <div
          class="border-border relative flex h-[140px] flex-col items-center justify-center gap-2 rounded-[8px] border p-4">
          <span class="truncate text-center text-[12px] font-[500]">{{ pharmacistNationalIdFile.name }}</span>
          <span class="text-neutral-text text-[10px]">
            ({{ (pharmacistNationalIdFile.size || 0) / 1024 / 1024 | number: "1.2-2" }} MB)
          </span>
          <button
            type="button"
            (click)="removeFile('nationalId')"
            class="text-primary hover-text-primary-hover hover:cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M7 21q-.825 0-1.412-.587T5 19V6H4V4h5V3h6v1h5v2h-1v13q0 .825-.587 1.413T17 21zM17 6H7v13h10zM9 17h2V8H9zm4 0h2V8h-2zM7 6v13z" />
            </svg>
          </button>
        </div>
      }
      @if (pharmacistNationalIdError) {
        <span class="text-danger-text text-[10px]">{{ pharmacistNationalIdError }}</span>
      }
    </div>
  </div>

  @if (submissionError) {
    <div class="text-danger-text rounded-[6px] bg-red-50 p-4">
      <p class="text-[14px] font-[500]">{{ submissionError }}</p>
    </div>
  }
  <div class="mt-4 flex justify-end gap-3">
    <button
      type="button"
      (click)="onCancel()"
      class="border-primary text-primary hover-bg-primary-10 rounded-[6px] border px-4 py-2 text-[12px] font-[500] hover:cursor-pointer"
      i18n="@@pharmacyFormCancelButton">
      Cancel
    </button>
    <button
      type="submit"
      [disabled]="loading()"
      class="bg-primary hover-bg-primary-hover disabled-bg-disabled-bg disabled-text-disabled-text rounded-[6px] px-4 py-2 text-[12px] font-[500] text-white hover:cursor-pointer disabled:cursor-not-allowed">
      @if (loading()) {
        <span i18n="@@pharmacyFormUpdatingButton">Updating...</span>
      } @else {
        <span i18n="@@pharmacyFormUpdateButton">Update Pharmacy</span>
      }
    </button>
  </div>
</form>
